<template>
  <div class="h-full flex flex-col bg-gray-50 dark:bg-slate-900 overflow-hidden">
    
    <!-- État de chargement initial -->
    <div 
      v-if="isLoading && !conversations.length" 
      class="flex-1 flex flex-col justify-center items-center gap-3 py-12">
      <div class="relative">
        <div class="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
      <p class="text-sm text-gray-500 dark:text-gray-400">Chargement des conversations...</p>
    </div>

    <!-- État d'erreur -->
    <div 
      v-else-if="!isSuccess"
      class="flex-1 flex flex-col justify-center items-center gap-4 py-12 px-4">
      <div class="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
        <i class="las la-exclamation-triangle text-3xl text-red-500 dark:text-red-400"></i>
      </div>
      <div class="text-center">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Erreur de chargement
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Impossible de charger les conversations
        </p>
        <button 
          @click="getConversations"
          class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors duration-200">
          <i class="las la-redo-alt mr-1"></i>
          Réessayer
        </button>
      </div>
    </div>

    <!-- État vide -->
    <div 
      v-else-if="!conversations.length"
      class="flex-1 flex flex-col justify-center items-center gap-4 py-12 px-4">
      <div class="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
        <i class="las la-comments text-4xl text-gray-400 dark:text-gray-500"></i>
      </div>
      <div class="text-center">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Aucune conversation
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Les nouvelles conversations apparaîtront ici
        </p>
      </div>
    </div>

    <!-- Liste des conversations -->
    <div 
      v-else
      ref="conversations"
      class="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
      @scroll="debounceEffect"
    >
      
      <div class="divide-y divide-gray-200 dark:divide-gray-700">
        <ConversationItem 
          v-for="conversation in conversations" 
          :key="conversation.id" 
          :conversation="conversation" 
        />
      </div>

      <!-- Loader pagination -->
      <div 
        v-if="isLoading && conversations.length" 
        class="flex justify-center items-center py-4">
        <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <div class="w-4 h-4 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
          <span>Chargement...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields';
import { mapActions } from 'vuex';
import ConversationItem from './ConversationItem.vue';

export default {
  name: "InboxConversations",
  components: {
    ConversationItem
  },
  data() {
    return {
      timeoutId: null
    }
  },
  computed: {
    ...mapFields("conversations/list", {
      isLoading: "isLoading",
      conversations: "items",
      isSuccess: "isSuccess",
      message: "message",
      showMessage: "showMessage",
      filterSearch: "filterSearch"
    }),
    ...mapFields("conversations/update", {
      conversationIsUpdated: "isUpdated",
      conversationReload: "reloadConversation",
    }),
  },
  methods: {
    ...mapActions({
      getConversations: "conversations/list/getAll",
      getConversation: "conversations/show/getOne",
      getPagination: "conversations/list/getPagination",
      resetMessages: "messages/list/resetData",
      resetReply: "messages/reply/resetData",
      resetConversations: "conversations/list/resetData",
      resetConversation: "conversations/show/resetData",
    }),
    
    debounceEffect(event) {
      // Empêcher la propagation du scroll au parent
      event.stopPropagation();
      
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      
      this.timeoutId = setTimeout(() => {
        if (this.isLoading) return;
        const container = this.$refs.conversations;
        if (!container) return;
        
        // Détection du scroll en bas avec une marge de 50px
        if (container.scrollHeight - container.scrollTop - container.clientHeight < 50) {
          this.getPagination();
        }
      }, 300);
    },
  },
  watch: {
    conversationIsUpdated(val) {
      if (val) {
        // Ne plus recharger la conversation ni la liste depuis l'API
        // La mise à jour est déjà faite directement par updateConversationDirect
        // et la liste sera mise à jour par les événements WebSocket
        // if (this.conversationReload) {
        //   this.getConversation();
        // } else {
        //   this.resetConversation();
        //   this.resetMessages();
        //   this.resetReply();
        // }
        // this.getConversations();
      }
    },
  },
  mounted() {
    this.getConversations();
  },
  beforeUnmount() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.resetConversations();
  }
}
</script>

<style scoped>
/* Empêcher le scroll horizontal et vertical au-delà du conteneur */
.overflow-x-hidden {
  overflow-x: hidden;
}

/* Custom scrollbar pour une meilleure UX */
.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  @apply bg-gray-300 dark:bg-gray-600 rounded-full;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400 dark:bg-gray-500;
}
</style>